import { useState, useMemo, useEffect, useRef } from "react";
import {
  FolderOpen,
  Folder,
  Image as ImageIcon,
  Video as VideoIcon,
  Plus,
  Trash2,
  Upload,
  Wrench,
  LifeBuoy,
  MessageSquare,
  ChevronLeft,
  Lock,
  ExternalLink,
  Search,
  X,
  Camera,
  Download,
  Check,
  Mic,
  Sparkles,
  Activity,
  ChevronRight,
} from "lucide-react";
import api from "../../lib/api";
import { uploadFile, uploadUrl } from "../../lib/uploads";
import { brandSlug } from "../../lib/brand";
import {
  uploadManyMedia,
  signedMediaUrl,
  downloadMediaToDevice,
  downloadMediaAsZip,
  bulkMoveMedia,
} from "../../lib/mediaR2";
import {
  queueMediaUpload,
  drainMediaQueue,
  subscribeMediaQueue,
  getMediaQueueCount,
} from "../../lib/mediaOfflineQueue";
import { preprocessFiles } from "../../lib/mediaCompress";
import VoiceRecorder from "../VoiceRecorder";
import FoldBlock from "./FoldBlock";
import MediaLightbox from "../MediaLightbox";
import Modal from "../Modal";
import ConfirmModal from "../ConfirmModal";
import UploadScanBadge from "../uploads/UploadScanBadge";
import { shareFile } from "../../lib/shareFile";
import { useShareable } from "../../hooks/useShareable";

// Wave 30 Chunk 10v — derive the upload id (fid) from a gallery item.
// Gallery items wrap an upload via `file_url`; the upload's id is either
// the raw value (legacy) or the trailing segment of `/api/uploads/{id}`.
function uploadIdOf(item) {
  if (!item) return null;
  if (item.r2_key && item.id) return item.id; // R2-native items: id === upload fid
  const url = item.file_url || "";
  if (!url) return item.upload_id || null;
  if (url.startsWith("/api/uploads/")) return url.replace("/api/uploads/", "").split(/[/?#]/)[0];
  if (url.startsWith("/uploads/")) return url.replace("/uploads/", "").split(/[/?#]/)[0];
  return url; // bare fid case
}

/**
 * Gallery block — folders containing images / videos.
 * Auto-folders:
 *  - One folder per Job Card with photos: "JC#0379 · 12 Apr 2026" (read-only)
 *  - One folder per Ticket with attachments: "ST#0149 · 14 Apr 2026" (read-only)
 *
 * Manual folders + items are stored in /gallery-folders + /gallery-items.
 *
 * Props:
 *  customerId, folders, items, jobcards, tickets
 *  canEdit (admin/manager) → can add manual folders/items, edit, delete anywhere
 *  canUpload (admin/manager OR client) → can add manual folders/items
 *  canDelete (admin/manager) → can delete; clients can only request removal
 *  canRequestRemoval (client) → request manager-side removal
 *  onChanged, onRequestRemoval
 */
export default function GalleryBlock({
  customerId,
  folders = [],
  items = [],
  jobcards = [],
  tickets = [],
  chats = [],
  canEdit = false,
  canUpload = false,
  canDelete = false,
  canRequestRemoval = false,
  isPortal = false,
  defaultOpen = false,
  filterToWorkOnly = false,
  onChanged,
  onRequestRemoval,
}) {
  const [openFolder, setOpenFolder] = useState(null); // { auto: bool, id, name, items, sourceType?, sourceId? }
  // Iter 418.ag (Dirk, 2026-02-28) — category-first navigation. Root
  // view shows one tile per category (Job Cards / Support Tickets /
  // Performance / Chats) + any un-categorised manual folders. Tapping
  // a category tile drills into its folders; back button returns.
  const [openCategory, setOpenCategory] = useState(null); // null | "jobcard" | "ticket" | "performance" | "chat" | "all"
  // [Iter 433 · Dirk 2026-02-28] "All media" is a virtual category:
  // flat flattening of every photo/video on this customer. The user
  // asked for a "full, full, full" view.
  const [folderFilter, setFolderFilter] = useState("");
  // [Iter 433 · sort] Shared sort control for "All media" + folder
  // views. Options match the staff /gallery page for consistency.
  const [sortBy, setSortBy] = useState("newest");
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [folderName, setFolderName] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmDelFolder, setConfirmDelFolder] = useState(null);
  const [confirmDelItem, setConfirmDelItem] = useState(null);
  const [search, setSearch] = useState("");

  // Build virtual auto-folders from JCs / Tickets that have media
  const autoFolders = useMemo(() => {
    const out = [];
    jobcards.forEach((j) => {
      const photos = (j.photos || []).filter(Boolean);
      // Treat checklist items' photos too
      const cli = (j.checklist || []).flatMap((c) => c.photos || []);
      const all = [...photos, ...cli];
      if (all.length === 0) return;
      out.push({
        auto: true,
        id: `jc-${j.id}`,
        name: `${j.short_id ? `#${j.short_id} · ` : ""}${j.title || "Job card"}`,
        date: j.scheduled_at || j.created_at,
        kind: "jobcard",
        sourceId: j.id,
        items: all.map((src, i) => ({
          id: `jc-${j.id}-${i}`,
          file_url: src,
          file_name: `photo-${i + 1}`,
          media_type: "image",
        })),
      });
    });
    tickets.forEach((t) => {
      const ph = (t.photos || []).filter(Boolean);
      const att = (t.attachments || []).filter((a) => a && a.id);
      if (ph.length === 0 && att.length === 0) return;
      // Build a unified items array: legacy data-URL photos first, then structured attachments.
      const items = [];
      ph.forEach((src, i) => {
        items.push({
          id: `ticket-${t.id}-photo-${i}`,
          file_url: src,
          file_name: `photo-${i + 1}`,
          media_type: "image",
        });
      });
      att.forEach((a) => {
        const ct = a.content_type || "";
        const isVideo = ct.startsWith("video/") || a.kind === "video";
        const isImage = ct.startsWith("image/") || a.kind === "image";
        items.push({
          id: `ticket-${t.id}-att-${a.id}`,
          file_url: a.id,
          file_name: a.filename || a.id,
          media_type: isVideo ? "video" : isImage ? "image" : "other",
          content_type: ct,
        });
      });
      out.push({
        auto: true,
        id: `ticket-${t.id}`,
        name: `${t.short_id ? `#${t.short_id} · ` : ""}${t.subject || "Ticket"}`,
        date: t.created_at,
        kind: "ticket",
        sourceId: t.id,
        items,
      });
    });
    // [Iter 320 — 2026-02-20] Chat threads with media. Each thread
    // becomes its own read-only auto-folder alongside the JC / Ticket
    // ones so photos shared in `/chat/:tid` are discoverable from the
    // Gallery block instead of being buried in the chat history.
    //
    // [Iter 326 — 2026-02-24] PWA bug fix. We previously piped
    // `m.upload_url` in as `file_url` — that's the URL stored in Mongo
    // at message-send time with `?auth=<jwt>` baked in. Once that JWT
    // expired (or the secret rotated), every chat photo shown in the
    // Gallery/Portal would 401 → broken tile on PWA. Now we pass the
    // bare `upload_id` so the tile's `uploadUrl()` re-stamps the URL
    // with the CURRENT live token at render time (same fix that
    // ChatMessage.jsx got in iter 323).
    (chats || []).forEach((c) => {
      const cItems = (c.items || []).filter(
        (m) => m && (m.upload_id || m.upload_url) && (m.kind === "image" || m.kind === "video"),
      );
      if (cItems.length === 0) return;
      out.push({
        auto: true,
        id: `chat-${c.id}`,
        name: `💬 ${c.title || "Chat"}`,
        date: c.last_message_at,
        kind: "chat",
        sourceId: c.id,
        items: cItems.map((m) => ({
          id: `chat-${c.id}-${m.id}`,
          // Prefer the bare upload_id — the tile will build a fresh
          // authed URL. Legacy fallback to upload_url for very old
          // chat rows that predate the upload_id column.
          file_url: m.upload_id || m.upload_url,
          file_name: m.upload_filename || (m.kind === "image" ? "chat photo" : "chat video"),
          media_type: m.kind === "video" ? "video" : "image",
          content_type: m.upload_content_type || "",
          created_at: m.created_at,
          author_name: m.author_name,
          upload_id: m.upload_id,
        })),
      });
    });
    return out.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
  }, [jobcards, tickets, chats]);

  // Manual folders → enrich with their items.
  // When `filterToWorkOnly` is true (technician scope), hide manual folders +
  // any rootItems that aren't linked to the work the user has access to.
  const manualFolders = useMemo(() => {
    if (filterToWorkOnly) return [];
    return folders.map((f) => ({
      auto: false,
      id: f.id,
      name: f.name,
      description: f.description,
      // Iter 418.ag — surface the mirror's source-kind so category
      // grouping can bucket Performance / mirrored JC folders under
      // the correct parent tile even though they live in gallery_folders.
      auto_source_kind: f.auto_source_kind || null,
      kind: f.auto_source_kind || "manual",
      created_by_role: f.created_by_role,
      items: items.filter((i) => i.folder_id === f.id),
    }));
  }, [folders, items, filterToWorkOnly]);

  const allowedJcIds = useMemo(
    () => new Set((jobcards || []).map((j) => j.id)),
    [jobcards],
  );
  const allowedTicketIds = useMemo(
    () => new Set((tickets || []).map((t) => t.id)),
    [tickets],
  );

  const rootItems = filterToWorkOnly
    ? (items || []).filter(
        (i) =>
          !i.folder_id &&
          ((i.linked_jobcard_id && allowedJcIds.has(i.linked_jobcard_id)) ||
            (i.linked_ticket_id && allowedTicketIds.has(i.linked_ticket_id))),
      )
    : (items || []).filter((i) => !i.folder_id);
  const totalItems =
    (items || []).length +
    autoFolders.reduce((s, f) => s + f.items.length, 0);

  const allFolders = [...manualFolders, ...autoFolders];

  // Iter 418.ag (Dirk, 2026-02-28) — bucket folders by source category
  // so the root Gallery view shows one parent tile per category
  // (Job Cards / Support Tickets / Performance / Chats) instead of
  // dumping 200 flat sub-folders in the customer's face. Manual
  // (user-typed) folders stay at the root under an "Other" bucket.
  const CATEGORIES = useMemo(() => [
    { key: "jobcard",     label: "Job Cards",       Icon: Wrench,        color: "#F59E0B" },
    { key: "ticket",      label: "Support Tickets", Icon: LifeBuoy,      color: "#F43F5E" },
    { key: "performance", label: "Performance",     Icon: Activity,      color: "#22D3EE" },
    { key: "chat",        label: "Chats",           Icon: MessageSquare, color: "#A78BFA" },
  ], []);
  const CATEGORY_KEYS = useMemo(() => new Set(CATEGORIES.map((c) => c.key)), [CATEGORIES]);

  const foldersByCategory = useMemo(() => {
    const map = { jobcard: [], ticket: [], performance: [], chat: [], other: [] };
    for (const f of allFolders) {
      const k = f.kind && CATEGORY_KEYS.has(f.kind) ? f.kind : "other";
      map[k].push(f);
    }
    // newest first inside each bucket
    Object.keys(map).forEach((k) => {
      map[k].sort((a, b) => new Date(b.date || b.updated_at || 0) - new Date(a.date || a.updated_at || 0));
    });
    return map;
  }, [allFolders, CATEGORY_KEYS]);

  // Apply search across folder name + item file names
  const term = search.trim().toLowerCase();
  const filteredFolders = !term
    ? allFolders
    : allFolders
        .map((f) => {
          const folderMatches = f.name?.toLowerCase().includes(term);
          const matchedItems = (f.items || []).filter((it) =>
            (it.file_name || it.title || "").toLowerCase().includes(term),
          );
          if (folderMatches) return f;
          if (matchedItems.length) return { ...f, items: matchedItems };
          return null;
        })
        .filter(Boolean);
  const filteredRootItems = !term
    ? rootItems
    : rootItems.filter((it) =>
        (it.file_name || it.title || "").toLowerCase().includes(term),
      );

  const createFolder = async () => {
    if (!folderName.trim()) return;
    setBusy(true);
    try {
      await api.post("/gallery-folders", {
        customer_id: customerId,
        name: folderName.trim(),
      });
      setFolderName("");
      setCreateFolderOpen(false);
      onChanged?.();
    } finally {
      setBusy(false);
    }
  };

  const delFolder = async () => {
    if (!confirmDelFolder) return;
    setBusy(true);
    try {
      await api.delete(`/gallery-folders/${confirmDelFolder.id}`);
      setConfirmDelFolder(null);
      onChanged?.();
    } finally {
      setBusy(false);
    }
  };

  const delItem = async () => {
    if (!confirmDelItem) return;
    setBusy(true);
    try {
      await api.delete(`/gallery-items/${confirmDelItem.id}`);
      setConfirmDelItem(null);
      onChanged?.();
    } finally {
      setBusy(false);
    }
  };

  return (
    <FoldBlock
      icon={ImageIcon}
      iconColor="#10B981"
      title="Gallery"
      count={totalItems}
      defaultOpen={defaultOpen}
      canEdit={canUpload}
      onAdd={() => setCreateFolderOpen(true)}
      addLabel="New folder"
      testId="block-gallery"
      emptyHint="No photos or videos yet."
      alwaysRenderChildren={canUpload || isPortal}
    >
      <div className="space-y-3">
        {/* Search + Sort */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#647087]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search folders, files, JC# or ticket#…"
              className="hv-input pl-8 pr-8 py-1.5 text-xs w-full"
              data-testid="gallery-search"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[#647087] hover:text-[#dde3ec]"
                title="Clear search"
                data-testid="gallery-search-clear"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <div className="flex items-center gap-1 text-[11px] text-[#a0aabf]">
            <span>Sort</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="hv-input text-[11px] py-1 px-2"
              data-testid="gallery-top-sort-select"
            >
              <option value="newest">Newest first</option>
              <option value="oldest">Oldest first</option>
              <option value="name">Name A→Z</option>
              <option value="size_desc">Most items first</option>
              <option value="size_asc">Fewest items first</option>
            </select>
          </div>
        </div>

        {/* Iter 418.ag — Category-first navigation.
            • When there is no search term AND no category is open,
              show ONE parent tile per category (Job Cards / Support
              Tickets / Performance / Chats) plus "Other" (manual
              folders). Tap → drill into that category's grid.
            • When a category is open, show a back-strip + the folder
              grid scoped to that category.
            • When the user is searching, we bypass the category
              layer and show a flat matches grid so results are
              instant / discoverable without knowing which bucket
              a folder lives in.
        */}
        {!term && !openCategory && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
            {/* [Iter 433 · Phase 2 customer-side] "All media" tile — flat
                view of every photo/video on this customer, agnostic of
                origin. Click opens the "all" category which renders a
                flat grid of all items from every folder. */}
            {(allFolders.length > 0 || rootItems.length > 0) && (
              <button
                type="button"
                onClick={() => setOpenCategory("all")}
                className="group border border-white/5 rounded-md overflow-hidden bg-white/[0.02] hover:border-[#1FB6C1]/50 transition-colors relative text-left"
                data-testid="gallery-category-tile-all"
              >
                <div
                  className="aspect-[4/3] flex items-center justify-center relative"
                  style={{ background: "#1FB6C10d" }}
                >
                  <ImageIcon className="w-9 h-9" style={{ color: "#1FB6C1", opacity: 0.7 }} />
                  <span className="absolute top-1 right-1 text-[9px] uppercase tracking-wider bg-black/60 text-white/80 px-1.5 py-0.5 rounded">
                    all
                  </span>
                </div>
                <div className="p-2 flex items-center gap-1.5">
                  <div className="min-w-0 flex-1">
                    <div className="text-[11px] font-semibold truncate">All media</div>
                    <div className="text-[9px] text-[#647087]">{totalItems} item{totalItems === 1 ? "" : "s"}</div>
                  </div>
                  <ChevronRight className="w-3.5 h-3.5 text-[#647087] group-hover:text-[#1FB6C1]" />
                </div>
              </button>
            )}
            {CATEGORIES.map((cat) => {
              const list = foldersByCategory[cat.key] || [];
              const folderCount = list.length;
              const itemCount = list.reduce((s, f) => s + (f.items?.length || 0), 0);
              if (folderCount === 0) return null;
              return (
                <button
                  key={cat.key}
                  type="button"
                  onClick={() => setOpenCategory(cat.key)}
                  className="group border border-white/5 rounded-md overflow-hidden bg-white/[0.02] hover:border-[#10B981]/40 transition-colors relative text-left"
                  data-testid={`gallery-category-tile-${cat.key}`}
                >
                  <div
                    className="aspect-[4/3] flex items-center justify-center relative"
                    style={{ background: `${cat.color}0d` }}
                  >
                    <cat.Icon className="w-9 h-9" style={{ color: cat.color, opacity: 0.7 }} />
                    <span className="absolute top-1 right-1 text-[9px] uppercase tracking-wider bg-black/60 text-white/80 px-1.5 py-0.5 rounded">
                      {folderCount} folder{folderCount === 1 ? "" : "s"}
                    </span>
                  </div>
                  <div className="p-2 flex items-center gap-1.5">
                    <div className="min-w-0 flex-1">
                      <div className="text-[11px] font-semibold truncate">{cat.label}</div>
                      <div className="text-[9px] text-[#647087]">{itemCount} item{itemCount === 1 ? "" : "s"}</div>
                    </div>
                    <ChevronRight className="w-3.5 h-3.5 text-[#647087] group-hover:text-[#10B981]" />
                  </div>
                </button>
              );
            })}
            {/* Manual / uncategorised folders — flat tiles alongside category tiles */}
            {(foldersByCategory.other || []).map((f) => (
              <FolderTile
                key={f.id}
                folder={f}
                onClick={() => setOpenFolder(f)}
                onDelete={canDelete && !f.auto ? () => setConfirmDelFolder(f) : undefined}
                onRequestRemoval={
                  !canDelete && canRequestRemoval && !f.auto
                    ? () => onRequestRemoval?.({ type: "gallery_folder", id: f.id, label: f.name })
                    : undefined
                }
              />
            ))}
            {/* Add tile always available at root */}
            {canUpload && (
              <button
                type="button"
                onClick={() => setCreateFolderOpen(true)}
                className="border-2 border-dashed border-white/10 rounded-md p-3 flex flex-col items-center justify-center gap-1 text-[#647087] hover:border-[#10B981]/40 hover:text-[#10B981] transition-colors min-h-[110px]"
                data-testid="gallery-new-folder-tile"
              >
                <Plus className="w-5 h-5" />
                <span className="text-[10px] uppercase tracking-wider">New folder</span>
              </button>
            )}
            {allFolders.length === 0 && (
              <div className="col-span-full text-xs text-[#647087] italic py-4 text-center">
                No folders yet. Photos from Job Cards, Tickets and Performance readings will appear here automatically.
              </div>
            )}
          </div>
        )}

        {/* [Iter 433] "All media" drill-in — flat grid of every photo
            on this customer with a category filter dropdown. */}
        {!term && openCategory === "all" && (() => {
          const filtered = folderFilter
            ? allFolders.filter((f) => f.kind === folderFilter)
            : allFolders;
          const flatItems = filtered.flatMap((f) =>
            (f.items || []).map((it) => ({ ...it, _parent: f.name, _parentKind: f.kind })),
          );
          const flatRoot = folderFilter ? [] : rootItems.map((it) => ({ ...it, _parent: "Loose", _parentKind: "other" }));
          const _sorter = (a, b) => {
            switch (sortBy) {
              case "oldest":    return new Date(a.created_at || 0) - new Date(b.created_at || 0);
              case "name":      return (a.file_name || "").localeCompare(b.file_name || "");
              case "size_desc": return (b.size || 0) - (a.size || 0);
              case "size_asc":  return (a.size || 0) - (b.size || 0);
              case "type":      return (a.media_type || "").localeCompare(b.media_type || "")
                                       || (new Date(b.created_at || 0) - new Date(a.created_at || 0));
              case "newest":
              default:          return new Date(b.created_at || 0) - new Date(a.created_at || 0);
            }
          };
          const allItems = [...flatItems, ...flatRoot].sort(_sorter);
          return (
            <div className="space-y-2" data-testid="gallery-category-view-all">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={() => { setOpenCategory(null); setFolderFilter(""); }}
                  className="hv-btn hv-btn-ghost text-xs"
                  data-testid="gallery-category-back"
                >
                  <ChevronLeft className="w-3.5 h-3.5" /> All categories
                </button>
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="flex items-center gap-1 text-[11px] text-[#a0aabf]">
                    <span>Filter</span>
                    <select
                      value={folderFilter}
                      onChange={(e) => setFolderFilter(e.target.value)}
                      className="hv-input text-[11px] py-1 px-2"
                      data-testid="gallery-all-filter-select"
                    >
                      <option value="">All sources</option>
                      {CATEGORIES.map((c) => (
                        <option key={c.key} value={c.key}>{c.label}</option>
                      ))}
                      <option value="manual">Manual folders</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-1 text-[11px] text-[#a0aabf]">
                    <span>Sort</span>
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="hv-input text-[11px] py-1 px-2"
                      data-testid="gallery-all-sort-select"
                    >
                      <option value="newest">Newest first</option>
                      <option value="oldest">Oldest first</option>
                      <option value="name">Name A→Z</option>
                      <option value="size_desc">Largest first</option>
                      <option value="size_asc">Smallest first</option>
                      <option value="type">By type</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="text-[11px] uppercase tracking-wider text-[#647087]">
                All media · {allItems.length} item{allItems.length === 1 ? "" : "s"}
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-1.5">
                {allItems.map((it) => {
                  const parentFolder = filtered.find((f) => f.name === it._parent);
                  const legacy = it.file_url && !it.file_url.startsWith("data:") && !it.r2_key
                    ? uploadUrl(it.file_url)
                    : it.file_url;
                  return (
                    <button
                      key={it.id}
                      type="button"
                      onClick={() => parentFolder && setOpenFolder(parentFolder)}
                      className="group relative aspect-square rounded-md overflow-hidden border border-white/5 bg-white/[0.02] hover:border-[#1FB6C1]/50"
                      data-testid={`gallery-all-tile-${it.id}`}
                      title={`${it._parent} · ${it.file_name || ""}`}
                    >
                      {it.media_type === "video" ? (
                        <VideoIcon className="w-6 h-6 absolute inset-0 m-auto text-[#647087]" />
                      ) : (
                        <img
                          src={legacy || ""}
                          alt=""
                          className="w-full h-full object-cover"
                          loading="lazy"
                          onError={(e) => { e.currentTarget.style.display = "none"; }}
                        />
                      )}
                      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="text-[9px] text-white/90 truncate">{it._parent}</div>
                      </div>
                    </button>
                  );
                })}
                {allItems.length === 0 && (
                  <div className="col-span-full text-xs text-[#647087] italic py-4 text-center">
                    No media matches that filter.
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* Drilled-in category view — show the child folders only */}
        {!term && openCategory && openCategory !== "all" && (() => {
          const cat = CATEGORIES.find((c) => c.key === openCategory);
          const _rawList = foldersByCategory[openCategory] || [];
          // [Iter 433] Inner search/filter inside a category.
          const _f = folderFilter.toLowerCase().trim();
          const _afterFilter = !_f ? _rawList : _rawList.filter((f) =>
            (f.name || "").toLowerCase().includes(_f),
          );
          const _folderSorter = (a, b) => {
            const ad = a.updated_at || a.created_at || "";
            const bd = b.updated_at || b.created_at || "";
            switch (sortBy) {
              case "oldest": return ad.localeCompare(bd);
              case "name":   return (a.name || "").localeCompare(b.name || "");
              case "size_desc": return (b.item_count || 0) - (a.item_count || 0);
              case "size_asc":  return (a.item_count || 0) - (b.item_count || 0);
              case "newest":
              case "type":
              default: return bd.localeCompare(ad);
            }
          };
          const list = [..._afterFilter].sort(_folderSorter);
          return (
            <div className="space-y-2" data-testid={`gallery-category-view-${openCategory}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={() => { setOpenCategory(null); setFolderFilter(""); }}
                  className="hv-btn hv-btn-ghost text-xs"
                  data-testid="gallery-category-back"
                >
                  <ChevronLeft className="w-3.5 h-3.5" /> All categories
                </button>
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="relative min-w-[180px]">
                    <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-[#647087]" />
                    <input
                      type="text"
                      value={folderFilter}
                      onChange={(e) => setFolderFilter(e.target.value)}
                      placeholder="Filter folders…"
                      className="hv-input text-[11px] py-1 pl-7 pr-7 w-full"
                      data-testid="gallery-category-filter-input"
                    />
                    {folderFilter && (
                      <button
                        type="button"
                        onClick={() => setFolderFilter("")}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-[#647087] hover:text-white"
                        aria-label="Clear filter"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-1 text-[11px] text-[#a0aabf]">
                    <span>Sort</span>
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="hv-input text-[11px] py-1 px-2"
                      data-testid="gallery-category-sort-select"
                    >
                      <option value="newest">Newest first</option>
                      <option value="oldest">Oldest first</option>
                      <option value="name">Name A→Z</option>
                      <option value="size_desc">Most items first</option>
                      <option value="size_asc">Fewest items first</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-[#647087]">
                {cat && <cat.Icon className="w-3.5 h-3.5" style={{ color: cat.color }} />}
                <span>{cat?.label} · {list.length}/{_rawList.length} folder{list.length === 1 ? "" : "s"}</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                {list.map((f) => (
                  <FolderTile
                    key={f.id}
                    folder={f}
                    onClick={() => setOpenFolder(f)}
                    onDelete={canDelete && !f.auto ? () => setConfirmDelFolder(f) : undefined}
                    onRequestRemoval={
                      !canDelete && canRequestRemoval && !f.auto
                        ? () => onRequestRemoval?.({ type: "gallery_folder", id: f.id, label: f.name })
                        : undefined
                    }
                  />
                ))}
                {list.length === 0 && (
                  <div className="col-span-full text-xs text-[#647087] italic py-4 text-center">
                    {folderFilter ? "No folders match that filter." : `No ${cat?.label.toLowerCase()} folders yet.`}
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* Search results — flat grid across ALL categories */}
        {term && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
            {filteredFolders.map((f) => (
              <FolderTile
                key={f.id}
                folder={f}
                onClick={() => setOpenFolder(f)}
                onDelete={canDelete && !f.auto ? () => setConfirmDelFolder(f) : undefined}
                onRequestRemoval={
                  !canDelete && canRequestRemoval && !f.auto
                    ? () => onRequestRemoval?.({ type: "gallery_folder", id: f.id, label: f.name })
                    : undefined
                }
              />
            ))}
            {filteredFolders.length === 0 && filteredRootItems.length === 0 && (
              <div className="col-span-full text-xs text-[#647087] italic py-4 text-center">
                No matches for &ldquo;{search}&rdquo;.
              </div>
            )}
          </div>
        )}

        {/* Root items (uploaded with no folder) */}
        {filteredRootItems.length > 0 && (
          <div className="border border-white/5 rounded-md p-3">
            <div className="text-[10px] uppercase tracking-wider text-[#647087] mb-2">
              Loose items ({filteredRootItems.length})
            </div>
            <ItemGrid
              items={filteredRootItems}
              canDelete={canDelete}
              canRequestRemoval={canRequestRemoval}
              onDelete={(it) => setConfirmDelItem(it)}
              onRequestRemoval={(it) =>
                onRequestRemoval?.({
                  type: "gallery_item",
                  id: it.id,
                  label: it.title || it.file_name,
                })
              }
              moveTargets={manualFolders}
              folderName="Loose items"
            />
          </div>
        )}

        {/* Quick upload-to-root */}
        {canUpload && !term && (
          <UploadDrop
            customerId={customerId}
            folderId={null}
            onUploaded={onChanged}
            label="Drop files here to upload to root"
          />
        )}
      </div>

      {/* Create-folder modal */}
      <Modal
        open={createFolderOpen}
        onClose={() => {
          setCreateFolderOpen(false);
          setFolderName("");
        }}
        title="New folder"
        testId="gallery-folder-modal"
        maxWidth="max-w-sm"
      >
        <div className="space-y-3">
          <div>
            <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Folder name</label>
            <input
              value={folderName}
              onChange={(e) => setFolderName(e.target.value)}
              placeholder="e.g. Reverse osmosis room"
              className="hv-input mt-1"
              autoFocus
              data-testid="gallery-folder-name"
            />
          </div>
          <button
            type="button"
            onClick={createFolder}
            disabled={busy || !folderName.trim()}
            className="hv-btn hv-btn-primary w-full"
            data-testid="gallery-folder-submit"
          >
            <Plus className="w-4 h-4" /> {busy ? "Creating…" : "Create folder"}
          </button>
        </div>
      </Modal>

      {/* Open-folder modal */}
      {openFolder && (
        <FolderModal
          folder={openFolder}
          customerId={customerId}
          onClose={() => setOpenFolder(null)}
          canUpload={canUpload && !openFolder.auto}
          canDelete={canDelete && !openFolder.auto}
          canRequestRemoval={canRequestRemoval && !openFolder.auto}
          moveTargets={manualFolders}
          onChanged={() => {
            // refresh the parent — and also bump the folder modal's items
            onChanged?.();
          }}
          onRequestRemoval={onRequestRemoval}
        />
      )}

      <ConfirmModal
        open={!!confirmDelFolder}
        onClose={() => setConfirmDelFolder(null)}
        onConfirm={delFolder}
        title="Delete folder"
        message={
          <>
            Permanently delete folder <b>{confirmDelFolder?.name}</b> and all its items?
          </>
        }
        confirmLabel="Delete folder"
        testId="gallery-folder-delete-confirm"
        busy={busy}
      />
      <ConfirmModal
        open={!!confirmDelItem}
        onClose={() => setConfirmDelItem(null)}
        onConfirm={delItem}
        title="Delete item"
        message={<>Permanently delete this item?</>}
        confirmLabel="Delete"
        testId="gallery-item-delete-confirm"
        busy={busy}
      />
    </FoldBlock>
  );
}

/* ---- Folder tile ---- */
function FolderTile({ folder, onClick, onDelete, onRequestRemoval }) {
  const cover = folder.items?.[0]?.file_url;
  const coverUrl = cover
    ? cover.startsWith("http") || cover.startsWith("data:")
      ? cover
      : uploadUrl(cover)
    : null;
  const KindIcon = folder.auto
    ? folder.kind === "ticket"
      ? LifeBuoy
      : folder.kind === "chat"
        ? MessageSquare
        : Wrench
    : Folder;
  return (
    <div
      className="group border border-white/5 rounded-md overflow-hidden bg-white/[0.02] hover:border-[#10B981]/40 transition-colors cursor-pointer relative"
      onClick={onClick}
      data-testid={`gallery-folder-tile-${folder.id}`}
    >
      <div
        className="aspect-[4/3] flex items-center justify-center relative"
        style={
          coverUrl
            ? { backgroundImage: `url(${coverUrl})`, backgroundSize: "cover", backgroundPosition: "center" }
            : { background: "rgba(16,185,129,0.05)" }
        }
      >
        {!coverUrl && <KindIcon className="w-8 h-8 text-[#10B981]/40" />}
        {folder.auto && (
          <span className="absolute top-1 left-1 text-[9px] uppercase tracking-wider bg-black/60 text-amber-300 px-1.5 py-0.5 rounded inline-flex items-center gap-1">
            <Lock className="w-2.5 h-2.5" /> Auto
          </span>
        )}
        <span className="absolute bottom-1 right-1 text-[10px] font-mono bg-black/60 text-[#dde3ec] px-1.5 py-0.5 rounded">
          {folder.items?.length || 0}
        </span>
      </div>
      <div className="px-2 py-1.5 flex items-center justify-between gap-1">
        <div className="text-xs font-medium truncate min-w-0">{folder.name}</div>
        {onDelete && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className="text-[#647087] hover:text-rose-300 opacity-0 group-hover:opacity-100"
            title="Delete folder"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        )}
        {!onDelete && onRequestRemoval && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onRequestRemoval();
            }}
            className="text-[#647087] hover:text-amber-300 opacity-0 group-hover:opacity-100"
            title="Request manager to remove"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        )}
      </div>
    </div>
  );
}

/* ---- Folder modal (open) ---- */
function FolderModal({ folder, customerId, onClose, canUpload, canDelete, canRequestRemoval, moveTargets = [], onChanged, onRequestRemoval }) {
  const [confirmDelItem, setConfirmDelItem] = useState(null);
  const [busy, setBusy] = useState(false);
  // [Iter 433 · Dirk] Sort control for items inside the folder.
  const [folderSort, setFolderSort] = useState("newest");

  // For manual folders we track items locally via parent prop refreshes (folder.items is a slice).
  const items = folder.items || [];

  const sortedItems = useMemo(() => {
    const _c = [...items];
    const _s = folderSort;
    _c.sort((a, b) => {
      switch (_s) {
        case "oldest":    return new Date(a.created_at || 0) - new Date(b.created_at || 0);
        case "name":      return (a.file_name || "").localeCompare(b.file_name || "");
        case "size_desc": return (b.size || 0) - (a.size || 0);
        case "size_asc":  return (a.size || 0) - (b.size || 0);
        case "type":      return (a.media_type || "").localeCompare(b.media_type || "")
                                 || (new Date(b.created_at || 0) - new Date(a.created_at || 0));
        case "newest":
        default:          return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      }
    });
    return _c;
  }, [items, folderSort]);

  const delItem = async () => {
    if (!confirmDelItem) return;
    setBusy(true);
    try {
      await api.delete(`/gallery-items/${confirmDelItem.id}`);
      setConfirmDelItem(null);
      onChanged?.();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={
        <span className="flex items-center gap-2">
          <button onClick={onClose} className="text-[#647087] hover:text-white">
            <ChevronLeft className="w-4 h-4" />
          </button>
          {folder.auto ? (
            <Lock className="w-3.5 h-3.5 text-amber-300" />
          ) : (
            <FolderOpen className="w-4 h-4 text-[#10B981]" />
          )}
          {folder.name}
          <span className="text-xs font-normal text-[#647087]">{items.length} items</span>
        </span>
      }
      testId="gallery-folder-modal-open"
      maxWidth="max-w-3xl"
    >
      <div className="space-y-3" data-testid="gallery-folder-content">
        {folder.auto && (
          <div className="text-[11px] text-amber-300 bg-amber-500/5 border border-amber-500/30 rounded px-3 py-2 flex items-start gap-2">
            <Lock className="w-3 h-3 mt-0.5 shrink-0" />
            <span>
              Auto-folder synced from {
                folder.kind === "ticket"
                  ? "support ticket"
                  : folder.kind === "chat"
                    ? "chat thread"
                    : "job card"
              }.
              {canDelete || canRequestRemoval
                ? " Manage source media on the original record."
                : ""}
            </span>
          </div>
        )}

        {/* Quick actions — download whole folder as zip */}
        {items.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <FolderZipButton folderName={folder.name} items={items} />
            <span className="text-[11px] text-[#647087]">
              {items.length} item{items.length === 1 ? "" : "s"}
            </span>
            <div className="flex items-center gap-1 text-[11px] text-[#a0aabf] ml-auto">
              <span>Sort</span>
              <select
                value={folderSort}
                onChange={(e) => setFolderSort(e.target.value)}
                className="hv-input text-[11px] py-1 px-2"
                data-testid="gallery-folder-sort-select"
              >
                <option value="newest">Newest first</option>
                <option value="oldest">Oldest first</option>
                <option value="name">Name A→Z</option>
                <option value="size_desc">Largest first</option>
                <option value="size_asc">Smallest first</option>
                <option value="type">By type</option>
              </select>
            </div>
          </div>
        )}

        {items.length === 0 ? (
          <div className="text-sm text-[#647087] italic py-4 text-center">
            Empty folder.
          </div>
        ) : (
          <ItemGrid
            items={sortedItems}
            canDelete={canDelete}
            canRequestRemoval={canRequestRemoval}
            onDelete={(it) => setConfirmDelItem(it)}
            onRequestRemoval={(it) =>
              onRequestRemoval?.({
                type: "gallery_item",
                id: it.id,
                label: it.title || it.file_name,
              })
            }
            moveTargets={moveTargets}
            currentFolderId={folder.id}
            folderName={folder.name}
          />
        )}

        {canUpload && !folder.auto && (
          <UploadDrop
            customerId={customerId}
            folderId={folder.id}
            onUploaded={onChanged}
            label="Drop files into this folder"
          />
        )}
      </div>

      <ConfirmModal
        open={!!confirmDelItem}
        onClose={() => setConfirmDelItem(null)}
        onConfirm={delItem}
        title="Delete item"
        message={<>Permanently delete this item?</>}
        confirmLabel="Delete"
        testId="gallery-folder-item-delete-confirm"
        busy={busy}
      />
    </Modal>
  );
}

/* ---- Item grid with R2 signed URLs + multi-select + download + move ---- */
function ItemGrid({
  items,
  canDelete,
  canRequestRemoval,
  onDelete,
  onRequestRemoval,
  moveTargets = [],
  currentFolderId = null,
  folderName = "",
}) {
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [zipProgress, setZipProgress] = useState(null);
  // Wave 29 Chunk 8c — phone-style prev/next viewer (Dirk bug 2026-05-11).
  // `lightboxItems` are the resolved [{src, kind}] for the current grid;
  // `lightboxIndex` is the focused index, or -1 when closed.
  const [lightboxItems, setLightboxItems] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(-1);

  // Resolve every grid item to a viewable URL once (signed for R2, legacy
  // for old uploads). Re-runs when the items array changes.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const resolved = await Promise.all((items || []).map(async (it) => {
        const k = it.kind || it.media_type || "photo";
        const isVideo = k === "video" || (it.content_type || "").startsWith("video/");
        if (!it.r2_key) {
          // [Iter 327] Smart uploadUrl handles bare-id / path / stale full URL.
          const u = it.file_url
            ? (it.file_url.startsWith("data:") ? it.file_url : uploadUrl(it.file_url))
            : "";
          return { src: u, kind: isVideo ? "video" : "image", caption: it.file_name };
        }
        try {
          const info = await signedMediaUrl(it.id, { thumb: false });
          return { src: info.url, kind: isVideo ? "video" : "image", caption: it.file_name };
        } catch {
          return { src: "", kind: isVideo ? "video" : "image", caption: it.file_name };
        }
      }));
      if (!cancelled) setLightboxItems(resolved);
    })();
    return () => { cancelled = true; };
  }, [items]);

  const clearSel = () => setSelected(new Set());
  const toggleSel = (id) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const selectAll = () => setSelected(new Set(items.map((i) => i.id)));

  // When items change (deleted / refreshed), drop any stale selections.
  useEffect(() => {
    setSelected((prev) => {
      const valid = new Set(items.map((i) => i.id));
      const next = new Set();
      for (const id of prev) if (valid.has(id)) next.add(id);
      return next;
    });
  }, [items]);

  const downloadSelected = async () => {
    setBusy(true);
    const selItems = items.filter((i) => selected.has(i.id));
    if (selItems.length > 1) {
      // Zip & single-download
      try {
        const stamp = new Date().toISOString().slice(0, 10);
        await downloadMediaAsZip(
          selItems,
          `${brandSlug()}-${folderName || "download"}-${stamp}.zip`,
          (done, total, pct) =>
            setZipProgress({ done, total, pct: Math.round(pct || (done / total) * 100) }),
        );
      } catch (e) {
        window.alert("Zip download failed: " + (e?.message || e));
      }
      setZipProgress(null);
    } else {
      // Single — no zip, just save as-is
      for (const it of selItems) {
        try {
          if (it.r2_key) await downloadMediaToDevice(it);
          else if (it.file_url) {
            const a = document.createElement("a");
            // [Iter 327] Smart uploadUrl handles stale full URLs.
            a.href = uploadUrl(it.file_url) || it.file_url;
            a.download = it.file_name || "download";
            a.target = "_blank";
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
          }
        } catch (e) {
          console.error("download failed", it.id, e);
        }
      }
    }
    setBusy(false);
  };

  const deleteSelected = async () => {
    if (!selected.size) return;
    if (!window.confirm(`Delete ${selected.size} item(s)? This cannot be undone.`)) return;
    setBusy(true);
    for (const id of selected) {
      const it = items.find((x) => x.id === id);
      if (!it) continue;
      try {
        if (it.r2_key) await api.delete(`/media/${id}`);
        else await api.delete(`/gallery-items/${id}`);
      } catch (e) {
        console.error("delete failed", id, e);
      }
    }
    clearSel();
    setBusy(false);
    window.dispatchEvent(new CustomEvent("gallery:refresh"));
  };

  const moveSelectedTo = async (targetFolderId) => {
    if (!selected.size) return;
    setBusy(true);
    try {
      await bulkMoveMedia(Array.from(selected), targetFolderId);
    } catch (e) {
      console.error("bulk move failed", e);
    }
    clearSel();
    setMoveOpen(false);
    setBusy(false);
    window.dispatchEvent(new CustomEvent("gallery:refresh"));
  };

  return (
    <div className="space-y-2">
      {selected.size > 0 && (
        <div
          className="sticky top-0 z-10 flex items-center justify-between gap-2 bg-[#121419]/95 backdrop-blur border border-[#1fb6c1]/30 rounded-md px-3 py-2 flex-wrap"
          data-testid="gallery-multiselect-bar"
        >
          <div className="text-xs text-[#1fb6c1] font-semibold">
            {selected.size} selected
          </div>
          <div className="flex items-center gap-1 flex-wrap">
            <button
              type="button"
              onClick={selectAll}
              disabled={selected.size === items.length}
              className="hv-btn hv-btn-ghost text-xs"
              data-testid="gallery-select-all"
            >
              Select all
            </button>
            <button
              type="button"
              onClick={downloadSelected}
              disabled={busy}
              className="hv-btn hv-btn-ghost text-xs"
              data-testid="gallery-download-selected"
            >
              <Download className="w-3 h-3" />{" "}
              {busy
                ? zipProgress
                  ? `Zipping ${zipProgress.done}/${zipProgress.total}…`
                  : "Downloading…"
                : selected.size > 1
                ? "Download zip"
                : "Download"}
            </button>
            {moveTargets.length > 0 && (
              <button
                type="button"
                onClick={() => setMoveOpen(true)}
                disabled={busy}
                className="hv-btn hv-btn-ghost text-xs"
                data-testid="gallery-move-selected"
              >
                <Folder className="w-3 h-3" /> Move
              </button>
            )}
            {canDelete && (
              <button
                type="button"
                onClick={deleteSelected}
                disabled={busy}
                className="hv-btn hv-btn-ghost text-xs !text-rose-300"
                data-testid="gallery-delete-selected"
              >
                <Trash2 className="w-3 h-3" /> Delete
              </button>
            )}
            <button
              type="button"
              onClick={clearSel}
              className="hv-btn hv-btn-ghost text-xs"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        </div>
      )}

      {moveOpen && (
        <Modal
          open
          onClose={() => setMoveOpen(false)}
          title={`Move ${selected.size} item(s) to…`}
          testId="gallery-move-modal"
          maxWidth="max-w-md"
        >
          <div className="space-y-2" data-testid="gallery-move-targets">
            <button
              type="button"
              disabled={!currentFolderId}
              onClick={() => moveSelectedTo(null)}
              className="w-full hv-btn hv-btn-ghost justify-start text-sm disabled:opacity-40"
              data-testid="gallery-move-to-root"
            >
              <FolderOpen className="w-4 h-4" /> (Root) — loose items
            </button>
            {moveTargets
              .filter((t) => t.id !== currentFolderId)
              .map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => moveSelectedTo(t.id)}
                  className="w-full hv-btn hv-btn-ghost justify-start text-sm"
                  data-testid={`gallery-move-target-${t.id}`}
                >
                  <Folder className="w-4 h-4" /> {t.name}
                </button>
              ))}
          </div>
        </Modal>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
        {items.map((it, idx) => (
          <GalleryTile
            key={it.id}
            item={it}
            selected={selected.has(it.id)}
            onToggle={() => toggleSel(it.id)}
            selectMode={selected.size > 0}
            canDelete={canDelete}
            canRequestRemoval={canRequestRemoval}
            onDelete={onDelete}
            onRequestRemoval={onRequestRemoval}
            onOpen={() => setLightboxIndex(idx)}
          />
        ))}
      </div>

      <MediaLightbox
        items={lightboxItems}
        index={lightboxIndex}
        onChange={setLightboxIndex}
        onClose={() => setLightboxIndex(-1)}
      />
    </div>
  );
}

/* Single gallery tile — resolves R2 signed URLs lazily so opening a
   folder doesn't fire 30 signing requests at once. */
function GalleryTile({
  item,
  selected,
  onToggle,
  selectMode,
  canDelete,
  canRequestRemoval,
  onDelete,
  onRequestRemoval,
  onOpen,
}) {
  const isR2 = !!item.r2_key;
  const kind = item.kind || item.media_type || "photo";
  const isVideo = kind === "video" || (item.content_type || "").startsWith("video/");
  const [signedUrl, setSignedUrl] = useState(null);
  const [busy, setBusy] = useState(false);

  // Legacy URL path (pre-R2 attachments)
  // [Iter 327] Smart uploadUrl handles bare-id / path / stale full URL.
  const legacyUrl = !isR2 && item.file_url
    ? (item.file_url.startsWith("data:") ? item.file_url : uploadUrl(item.file_url))
    : null;

  // For R2 items, fetch a signed URL on mount. Prefer the 600px webp
  // thumbnail when one exists — massively reduces data usage on 3G.
  useEffect(() => {
    if (!isR2) return;
    let cancelled = false;
    (async () => {
      try {
        const info = await signedMediaUrl(item.id, { thumb: !isVideo });
        if (!cancelled) setSignedUrl(info.url);
      } catch {
        // silently fall through — tile shows placeholder
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [item.id, isR2, isVideo]);

  const viewUrl = legacyUrl || signedUrl || "";

  const onTileClick = (e) => {
    if (selectMode) {
      e.preventDefault();
      onToggle();
      return;
    }
    // Wave 29 Chunk 8c — open in-app phone-style viewer (prev/next + swipe)
    // instead of a new browser tab.
    if (onOpen) {
      e.preventDefault();
      onOpen();
    }
  };

  const onLongPressStartRef = useRef(null);
  const onPressDown = () => {
    onLongPressStartRef.current = Date.now();
  };
  const onPressUp = (e) => {
    const dt = Date.now() - (onLongPressStartRef.current || 0);
    if (dt > 500) {
      e.preventDefault();
      onToggle();
    }
  };

  // Iter 187 — right-click (desktop) on the tile opens the share menu.
  // Long-press on mobile already maps to "select mode" (above), so we
  // surface long-press-to-share only when the user is NOT in select mode
  // and we route through the dedicated <ShareableMedia> wrapper around
  // the image element itself.
  const shareDescriptor = useMemo(() => {
    const url = legacyUrl || signedUrl || null;
    if (!url) return null;
    return {
      url,
      filename: item.file_name || item.filename,
      contentType: item.content_type,
      title: item.title || item.file_name || item.filename || "media",
    };
  }, [legacyUrl, signedUrl, item]);
  const { handlers: shareHandlers, menu: shareMenu } = useShareable(
    shareDescriptor,
    { disabled: selectMode || !shareDescriptor },
  );

  return (
    <div
      className={`group relative border rounded overflow-hidden bg-black/30 ${
        selected ? "border-[#1fb6c1] ring-2 ring-[#1fb6c1]/40" : "border-white/5"
      }`}
      data-testid={`gallery-item-${item.id}`}
      onMouseDown={onPressDown}
      onTouchStart={onPressDown}
      onMouseUp={onPressUp}
      onTouchEnd={onPressUp}
      onContextMenu={shareHandlers.onContextMenu}
    >
      {shareMenu}
      {/* Select checkbox (always visible on mobile, hover on desktop) */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          onToggle();
        }}
        className={`absolute top-1 left-1 z-10 w-5 h-5 rounded-md border flex items-center justify-center text-[10px] transition-colors ${
          selected
            ? "bg-[#1fb6c1] border-[#1fb6c1] text-white"
            : "bg-black/60 border-white/40 text-transparent md:opacity-0 md:group-hover:opacity-100"
        }`}
        data-testid={`gallery-item-select-${item.id}`}
        aria-label={selected ? "Deselect" : "Select"}
      >
        {selected ? <Check className="w-3 h-3" /> : null}
      </button>

      <a
        href={viewUrl || "#"}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onTileClick}
        className="block"
      >
        {viewUrl ? (
          isVideo ? (
            <video src={viewUrl} className="w-full aspect-square object-cover" />
          ) : (
            <img
              src={viewUrl}
              alt={item.filename || item.file_name || "media"}
              className="w-full aspect-square object-cover"
              loading="lazy"
            />
          )
        ) : (
          <div className="w-full aspect-square flex items-center justify-center bg-white/5">
            <ImageIcon className="w-6 h-6 text-[#647087]" />
          </div>
        )}
      </a>

      {/* Hover action cluster (always visible on mobile, hover-revealed on desktop) */}
      <div className="absolute top-1 right-1 flex gap-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
        <button
          type="button"
          onClick={async (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (busy) return;
            setBusy(true);
            try {
              if (isR2) await downloadMediaToDevice(item);
              else if (legacyUrl) {
                const a = document.createElement("a");
                a.href = legacyUrl;
                a.download = item.file_name || "download";
                a.target = "_blank";
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
              }
            } catch (err) {
              window.alert("Download failed: " + (err?.message || err));
            } finally {
              setBusy(false);
            }
          }}
          className="bg-black/70 text-[#1fb6c1] rounded p-1"
          title="Download to this device"
          data-testid={`gallery-item-download-${item.id}`}
        >
          <Download className="w-3 h-3" />
        </button>
        <button
          type="button"
          onClick={async (e) => {
            e.preventDefault();
            e.stopPropagation();
            try {
              // Iter 187 — share the FILE bytes, not a URL. Web Share API
              // attaches the real file in the OS share sheet on
              // iOS/Android; desktop falls back to a download + toast.
              let shareUrl = legacyUrl;
              let contentType = item.content_type;
              if (isR2) {
                const info = await signedMediaUrl(item.id, { thumb: false });
                shareUrl = info?.url;
                contentType = info?.content_type || contentType;
              }
              if (!shareUrl) return;
              await shareFile({
                url: shareUrl,
                filename: item.file_name || item.filename,
                contentType,
                title: item.title || item.file_name || item.filename || "media",
              });
            } catch (err) {
              if (err?.name !== "AbortError") {
                // eslint-disable-next-line no-console
                console.warn("share failed", err);
              }
            }
          }}
          className="bg-black/70 text-emerald-300 rounded p-1"
          title="Share file"
          data-testid={`gallery-item-share-${item.id}`}
        >
          <Upload className="w-3 h-3" />
        </button>
        {canDelete && (() => {
          const fname = (item.file_name || "").toLowerCase();
          const ingestable = fname.endsWith(".pdf") || fname.endsWith(".txt") || fname.endsWith(".md") || fname.endsWith(".csv");
          if (!ingestable) return null;
          return (
            <button
              type="button"
              onClick={async (e) => {
                e.stopPropagation();
                e.preventDefault();
                try {
                  const r = await api.post(`/doc-qa/ingest/${item.id}`);
                  window.alert(`Ingested ${r.data?.chunks || 0} chunks into Hidro Q&A.`);
                } catch (err) {
                  window.alert("Ingest failed: " + (err?.response?.data?.detail || err?.message || err));
                }
              }}
              className="bg-black/70 text-[#A78BFA] rounded p-1"
              title="Add to Hidro Q&A index"
              data-testid={`gallery-item-ingest-${item.id}`}
            >
              <Sparkles className="w-3 h-3" />
            </button>
          );
        })()}
        {canDelete && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onDelete(item);
            }}
            className="bg-black/70 text-rose-300 rounded p-1"
            title="Delete"
            data-testid={`gallery-item-delete-${item.id}`}
          >
            <Trash2 className="w-3 h-3" />
          </button>
        )}
        {!canDelete && canRequestRemoval && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onRequestRemoval(item);
            }}
            className="bg-black/70 text-amber-300 rounded p-1"
            title="Request manager removal"
            data-testid={`gallery-item-request-removal-${item.id}`}
          >
            <Trash2 className="w-3 h-3" />
          </button>
        )}
        {viewUrl && (
          <a
            href={viewUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="bg-black/70 text-white rounded p-1"
            title="Open"
          >
            <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>

      {isVideo && (
        <span className="absolute bottom-1 left-1 bg-black/70 text-white text-[9px] uppercase rounded px-1">
          <VideoIcon className="w-3 h-3 inline" />
        </span>
      )}

      {/* Wave 30 Chunk 10v — async Vision scan progress (only renders when pending/error) */}
      {!isVideo && uploadIdOf(item) && (
        <span className="absolute bottom-1 right-1 pointer-events-auto">
          <UploadScanBadge
            uploadId={uploadIdOf(item)}
            initialStatus={item.vision_describe_status || null}
            compact
          />
        </span>
      )}
    </div>
  );
}

/* Small reusable "Download folder as zip" button. */
function FolderZipButton({ folderName, items }) {
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);

  const run = async () => {
    setBusy(true);
    setProgress({ done: 0, total: items.length, pct: 0 });
    try {
      const stamp = new Date().toISOString().slice(0, 10);
      const safe = (folderName || "folder").replace(/[^a-z0-9-_]+/gi, "_");
      await downloadMediaAsZip(items, `${brandSlug()}-${safe}-${stamp}.zip`, (done, total, pct) =>
        setProgress({ done, total, pct: Math.round(pct || (done / total) * 100) }),
      );
    } catch (e) {
      window.alert("Zip download failed: " + (e?.message || e));
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  return (
    <button
      type="button"
      onClick={run}
      disabled={busy}
      className="hv-btn hv-btn-ghost text-xs"
      data-testid="gallery-folder-download-zip"
    >
      <Download className="w-3 h-3" />{" "}
      {busy
        ? progress
          ? `Zipping ${progress.done}/${progress.total} · ${progress.pct}%`
          : "Zipping…"
        : "Download folder"}
    </button>
  );
}

/* ---- Upload dropzone with camera/video capture for mobile ----
   Tries R2 first (direct-to-Cloudflare via presigned URL). If the server
   isn't configured, falls back to the legacy `/uploads` → `/gallery-items`
   pipeline. If the browser is OFFLINE, files are queued in IndexedDB and
   automatically uploaded when we're back online. */
function UploadDrop({ customerId, folderId, parentType, parentId, onUploaded, label }) {
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0, pct: 0 });
  const [dragOver, setDragOver] = useState(false);
  const [err, setErr] = useState("");
  const [queuedCount, setQueuedCount] = useState(0);
  const [voiceOpen, setVoiceOpen] = useState(false);
  const photoInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const filesInputRef = useRef(null);
  const audioInputRef = useRef(null);

  // Subscribe to the offline media queue so we show a "n queued" pill.
  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      const n = await getMediaQueueCount();
      if (!cancelled) setQueuedCount(n);
    };
    refresh();
    const off = subscribeMediaQueue(refresh);
    const onDrained = () => {
      refresh();
      onUploaded?.();
    };
    window.addEventListener("gallery:refresh", onDrained);
    return () => {
      cancelled = true;
      off();
      window.removeEventListener("gallery:refresh", onDrained);
    };
  }, [onUploaded]);

  const queueForLater = async (files) => {
    for (const f of files) {
      await queueMediaUpload(f, {
        customerId,
        folderId,
        parentType,
        parentId,
      });
    }
    return files.length;
  };

  const uploadViaR2 = async (files) => {
    const { succeeded, failed } = await uploadManyMedia(
      files,
      { customerId, folderId, parentType, parentId },
      (idx, pct, done, total) => {
        // Track aggregate batch progress: "3 of 7 done · current 84%"
        const aggPct = total > 0
          ? Math.round(((done * 100 + (pct || 0)) / total))
          : 0;
        setProgress({ done: done || 0, total: total || files.length, pct: Math.min(100, aggPct) });
      },
    );
    if (failed.length) {
      // Anything that failed — stash in offline queue so it retries later.
      await queueForLater(failed.map((f) => f.file));
      setErr(
        `${failed.length} of ${files.length} uploads deferred — will retry automatically.`,
      );
    }
    return succeeded.length;
  };

  const uploadViaLegacy = async (files) => {
    let done = 0;
    for (const f of files) {
      const up = await uploadFile(f);
      await api.post("/gallery-items", {
        customer_id: customerId,
        folder_id: folderId,
        file_url: up.id,
        file_name: up.filename,
        file_size: up.size,
        media_type: up.kind === "video" ? "video" : up.kind === "image" ? "image" : "other",
      });
      done++;
      setProgress({ done, total: files.length, pct: Math.round((done / files.length) * 100) });
    }
    return done;
  };

  const upload = async (rawFiles) => {
    if (!rawFiles?.length) return;
    setErr("");
    setBusy(true);
    setProgress({ done: 0, total: rawFiles.length, pct: 0 });
    try {
      // Preprocess images: EXIF auto-rotate + compress to ~1.5MB max edge.
      // Videos & audio pass through untouched (lossless).
      const files = await preprocessFiles(rawFiles);
      // Offline — queue everything and bail.
      if (!navigator.onLine) {
        const n = await queueForLater(files);
        setErr(`No signal — ${n} file(s) saved locally. They'll upload automatically when you're back online.`);
        return;
      }
      let r2 = false;
      try {
        const { data } = await api.get("/media/status");
        r2 = !!data?.r2_configured;
      } catch {
        r2 = false;
      }
      if (r2) await uploadViaR2(files);
      else await uploadViaLegacy(files);
      onUploaded?.();
    } catch (e) {
      // Something went wrong mid-batch — queue whatever didn't succeed.
      await queueForLater(rawFiles);
      setErr(
        (e?.message || "Upload failed") +
          ` — ${rawFiles.length} file(s) saved locally for retry.`,
      );
    } finally {
      setBusy(false);
      setProgress({ done: 0, total: 0, pct: 0 });
    }
  };

  const drainNow = async () => {
    setBusy(true);
    try {
      await drainMediaQueue();
      onUploaded?.();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2" data-testid="gallery-upload-drop">
      {/* Quick action buttons — super-simple, camera-first on mobile */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <button
          type="button"
          onClick={() => photoInputRef.current?.click()}
          disabled={busy}
          className="hv-btn hv-btn-ghost flex items-center justify-center gap-1 py-3 text-xs"
          data-testid="gallery-upload-photo"
        >
          <Camera className="w-4 h-4" /> Photo
        </button>
        <button
          type="button"
          onClick={() => videoInputRef.current?.click()}
          disabled={busy}
          className="hv-btn hv-btn-ghost flex items-center justify-center gap-1 py-3 text-xs"
          data-testid="gallery-upload-video"
        >
          <VideoIcon className="w-4 h-4" /> Video
        </button>
        <button
          type="button"
          onClick={() => filesInputRef.current?.click()}
          disabled={busy}
          className="hv-btn hv-btn-ghost flex items-center justify-center gap-1 py-3 text-xs"
          data-testid="gallery-upload-files"
        >
          <Upload className="w-4 h-4" /> Files
        </button>
        <button
          type="button"
          onClick={() => setVoiceOpen(true)}
          disabled={busy}
          className="hv-btn hv-btn-ghost flex items-center justify-center gap-1 py-3 text-xs"
          data-testid="gallery-record-voice"
        >
          <Mic className="w-4 h-4" /> Voice
        </button>
      </div>

      {/* Hidden capture/upload inputs */}
      <input
        ref={audioInputRef}
        type="file"
        accept="audio/*"
        multiple
        className="hidden"
        onChange={(e) => upload(Array.from(e.target.files || []))}
      />
      <input
        ref={photoInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        className="hidden"
        onChange={(e) => upload(Array.from(e.target.files || []))}
        data-testid="gallery-upload-input"
      />
      <input
        ref={videoInputRef}
        type="file"
        accept="video/*"
        capture="environment"
        multiple
        className="hidden"
        onChange={(e) => upload(Array.from(e.target.files || []))}
        data-testid="gallery-upload-video-input"
      />
      <input
        ref={filesInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => upload(Array.from(e.target.files || []))}
      />

      {/* Drag-drop surface (desktop) */}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          upload(Array.from(e.dataTransfer.files));
        }}
        className={`hidden md:block relative border-2 border-dashed rounded-md py-3 px-3 text-center text-xs transition-colors ${
          dragOver ? "border-[#10B981] bg-[#10B981]/5" : "border-white/10 bg-white/[0.02]"
        }`}
      >
        {busy ? (
          <span className="text-[#10B981]">
            Uploading {progress.done}/{progress.total} · {progress.pct}%
          </span>
        ) : (
          <span className="text-[#a0aabf]">{label} — or drop files here</span>
        )}
      </div>

      {busy && (
        <div className="h-1 bg-white/5 rounded overflow-hidden">
          <div
            className="h-full bg-[#10B981] transition-all"
            style={{ width: `${progress.pct || 0}%` }}
          />
        </div>
      )}
      {queuedCount > 0 && (
        <div
          className="flex items-center justify-between gap-2 text-[11px] bg-amber-500/10 border border-amber-500/30 rounded px-3 py-2"
          data-testid="gallery-offline-queue-banner"
        >
          <span className="text-amber-200">
            {queuedCount} file{queuedCount === 1 ? "" : "s"} queued offline —
            will upload automatically when you're back online.
          </span>
          <button
            type="button"
            onClick={drainNow}
            disabled={busy}
            className="hv-btn hv-btn-ghost text-xs"
            data-testid="gallery-drain-queue"
          >
            Try now
          </button>
        </div>
      )}
      {err && <div className="text-[11px] text-rose-300">{err}</div>}

      {voiceOpen && (
        <Modal
          open
          onClose={() => setVoiceOpen(false)}
          title="Record voice note"
          maxWidth="max-w-sm"
          testId="gallery-voice-modal"
        >
          <VoiceRecorder
            autoStart
            label="Voice note"
            onCancel={() => setVoiceOpen(false)}
            onRecorded={async (blob, dur) => {
              const ext = (blob.type.split("/")[1] || "webm").split(";")[0];
              const file = new File(
                [blob],
                `voice-${new Date().toISOString().replace(/[:.]/g, "-")}.${ext}`,
                { type: blob.type, lastModified: Date.now() },
              );
              setVoiceOpen(false);
              await upload([file]);
            }}
          />
        </Modal>
      )}
    </div>
  );
}
